import Controller, { OnBeforeSubmit, OnClick, OnMount, OnUnmount, OnValidate } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, CheckboxAPI, FormAPI, InputAPI, ModalProps, ModalService, ToastService, WizardAPI } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScoreTiss_28EditAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { ApplicationRoute } from '../../../../constants/routes';
import { Value } from '@philips-internal/oswald-ui-core';
import { DomainType, ScoreTISS28Response } from '../../score-types';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { HealthProfessionalUser } from '../../../../app/authentication';
import DialogService from '../../../../services/dialog-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { ScoreListContext } from '../../scores-list/scores-list-service';
import { WizardSteps } from '../../../../constants/wizards';

interface ResponseCreateScoreTISS28 {
  acidoseAlcalose?: string;
  attendance?: number;
  cateterArtPerif?: string;
  curativoFrequente?: string;
  curativoRotina?: string;
  diureseAtiva?: string;
  dreno?: string;
  evaluationDate?: string;
  idScoreIndexes: number;
  interForaUti?: string;
  interMultiUti?: string;
  interUnicaUti?: string;
  laboratorio?: string;
  levelAttention?: string;
  medDebitoUrina?: string;
  medicEndovenosa?: string;
  medicaUnica?: string;
  monitPadrao?: string;
  nutEnteral?: string;
  nutParent?: string;
  pessoaFisica?: string;
  pressaoIntracraniana?: string;
  repVolemica?: string;
  ressuscitCardio?: string;
  scMonitAtrioEsquerdo?: string;
  scoreQuantity?: number;
  situation?: string;
  suepVentSupl?: string;
  tecHemofiltracao?: string;
  time?: number;
  tratFuncaoPulmonar?: string;
  updateDate?: string;
  updateNrec?: string;
  username?: string;
  usuarioNREC?: string;
  vasoativaMulti?: string;
  vasoativaUnica?: string;
  ventMecanica?: string;
  viaVenosaCentral?: string;
  viasAerias?: string;
}

@Controller({ component: ScoreTiss_28EditAliases.SCORE_TISS_28_ADD_LAYOUT, fragment: FragmentNames.SCORE_TISS_28_EDIT })
export default class ScoreTiss28EditController extends BaseController {

  @Inject(ScoreTiss_28EditAliases.INPUT_SETUP_DATE)
  private setupDateInputApi!: InputAPI;

  @Inject(ScoreTiss_28EditAliases.FORM_THREE)
  private formThreeAPI!: FormAPI;

  @Inject(ScoreTiss_28EditAliases.FORM_ONE)
  private formOneAPI!: FormAPI;

  @Inject(ScoreTiss_28EditAliases.FORM_TWO)
  private formTwoAPI!: FormAPI;

  @Inject(ScoreTiss_28EditAliases.WIZARD)
  private wizard!: WizardAPI;

  @Inject(ScoreTiss_28EditAliases.ACTION_DELETE_SCORE_TISS_28)
  private actionDeleteAPI!: ActionAPI;

  @Inject(ScoreTiss_28EditAliases.ACTION_NEW_SCORE_TISS_28)
  private actionNewAPI!: ActionAPI;

  @Inject(ScoreTiss_28EditAliases.ACTION_REFERENCE_SCORE_TISS_28)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScoreTiss_28EditAliases.ACTION_RELEASE_SCORE_TISS_28)
  private actionReleaseAPI!: ActionAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_ACIDOSIS)
  private checkAcidosisAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_ACTIVE_DIURESIS)
  private checkActiveDiuresisAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_ARTIFICIAL_AIRWAY)
  private checkArtificialAirwayAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_CARDIO_RESSUSCITATION)
  private checkCardioRessuscitAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_CAT_ART_PERIPHERAL)
  private checkCatArtPeriAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_CENTRAL_VENOUS)
  private checkCentralVenousAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_DRAIN)
  private checkDrainAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_ENT_NUTRI)
  private checkEntNutriAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_FREQ_DRESSINGS)
  private checkFreqDressingAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_INTRACRANIAL_PRESSURE)
  private checkIntracranialAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_INTRAVENOUS_MED)
  private checkIntravenousMedAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_LAB)
  private checkLabAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_MECH_VENT)
  private checkMechVentAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_MONIT_ATRIUM)
  private checkMonitAtriumAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_MULTI_ICU)
  private checkMultiICUAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_MULTIVASO_MED)
  private checkMultivasoMedAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_OUT_ICU)
  private checkOutICUAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_PAREN_NUTRI)
  private checkParenNutriAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_REPO_VOL)
  private checkRepoVolAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_ROUTINE_DRESSINGS)
  private checkRoutineDressingAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_STANDARD_MONIT)
  private checkStandardMonitAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_SUP_VENT_SUPPORT)
  private checkSupVentSupportAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_TECH_HEMOFILTRATION)
  private checkTechHemoAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_TRAT_PULMONAR)
  private checkTratPulmonarAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_UNIQUE_ICU)
  private checkUniqueICUAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_UNIQUE_MEDICATION)
  private checkUniqueMedAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_URINE_DEBIT)
  private checkUrineDebitAPI!: CheckboxAPI;

  @Inject(ScoreTiss_28EditAliases.CHECKBOX_VASO_MED)
  private checkVasoMedAPI!: CheckboxAPI;

  @OnValidate(ScoreTiss_28EditAliases.FORM_ONE)
  handleOneValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_ONE, validation.valid);
  }

  @OnValidate(ScoreTiss_28EditAliases.FORM_TWO)
  handleTwoValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_TWO, validation.valid);
  }

  @OnValidate(ScoreTiss_28EditAliases.FORM_THREE)
  handleThreeValidate(validation: ValidateEvent): void {
    this.wizard.setStepDone(WizardSteps.STEP_THREE, validation.valid);
  }

  @OnMount({ target: ScoreTiss_28EditAliases.FORM_ONE })
  handleMountForm(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    this.formOneAPI.setLoading(true);
    this.loadEditionData(idScoreIndexes as number);
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReferenceAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);
    ScoreListContext.currentTab = ScoreType.SCORE_TISS_28
  }

  @OnUnmount({ target: ScoreTiss_28EditAliases.FORM_ONE })
  handleUnMountForm(): void {
    this.clearAbortController();
  }

  @OnMount({ target: ScoreTiss_28EditAliases.WIZARD })
  handleFormMount(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, patientId });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-dini-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private releaseTISS28Score(sequenceNumber: number): void {
    this.formOneAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.put(Endpoint.RELEASE_SCORE_TISS_28, { sequenceNumber }, { abortController })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formOneAPI.setLoading(false);
      });
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releaseTISS28Score(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private delete(sequenceNumber: number): void {
    this.formOneAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.delete(Endpoint.SCORE_TISS_28, {
      sequenceNumber
    }, {abortController}).then(() => {
      applicationContext.popNavigationItem();
    }).catch((err) => {
      this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => {
      this.formOneAPI.setLoading(false);
    });
  }

  private confirmDelete(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { applicationContext.popNavigationItem() },
      onClickOk: () => {this.delete(idScore);},
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  private getReference(): void {
    const scoreType = 'tiss-28';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  private loadEditionData(sequenceNumber: number): void {
    const abortController = this.setAbortController();
    HttpService.get(Endpoint.SCORE_TISS_28, { sequenceNumber }, {abortController})
      .then(response => {
        const record = response.body as ScoreTISS28Response;

        this.formOneAPI.setEditing(true);
        this.formOneAPI.validate();
        this.formOneAPI.setDisabled(true);

        this.formOneAPI.setData(record as unknown as Record<string, ScoreTISS28Response>);
        this.formTwoAPI.setData(record as unknown as Record<string, ScoreTISS28Response>);
        this.formThreeAPI.setData(record as unknown as Record<string, ScoreTISS28Response>);

        this.treatData(record);
        this.wizard.setStepDone(WizardSteps.STEP_ONE, true);
        this.wizard.setStepDone(WizardSteps.STEP_TWO, true);
        this.wizard.setStepDone(WizardSteps.STEP_THREE, true);
      })
      .catch((err) => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formOneAPI.setLoading(false);
        this.actionDeleteAPI.setEnabled(true);
        this.actionNewAPI.setEnabled(true);
        this.actionReferenceAPI.setEnabled(true);
        this.actionReleaseAPI.setEnabled(true);
      });
  }

  private treatData(record: ScoreTISS28Response): void {
    const acidoseAlcalose = BooleanUtils.isTrueValue(record.acidoseAlcalose);
    const cateterArtPerif = BooleanUtils.isTrueValue(record.cateterArtPerif);
    const curativoFrequente = BooleanUtils.isTrueValue(record.curativoFrequente);
    const curativoRotina = BooleanUtils.isTrueValue(record.curativoRotina);
    const diureseAtiva = BooleanUtils.isTrueValue(record.diureseAtiva);
    const dreno = BooleanUtils.isTrueValue(record.dreno);
    const interForaUti = BooleanUtils.isTrueValue(record.interForaUti);
    const interMultiUti = BooleanUtils.isTrueValue(record.interMultiUti);
    const interUnicaUti = BooleanUtils.isTrueValue(record.interUnicaUti);
    const laboratorio = BooleanUtils.isTrueValue(record.laboratorio);
    const medDebitoUrina = BooleanUtils.isTrueValue(record.medDebitoUrina);
    const medicEndovenosa = BooleanUtils.isTrueValue(record.medicEndovenosa);
    const medicaUnica = BooleanUtils.isTrueValue(record.medicaUnica);
    const monitPadrao = BooleanUtils.isTrueValue(record.monitPadrao);
    const nutEnteral = BooleanUtils.isTrueValue(record.nutEnteral);
    const nutParent = BooleanUtils.isTrueValue(record.nutParent);
    const pressaoIntracraniana = BooleanUtils.isTrueValue(record.pressaoIntracraniana);
    const repVolemica = BooleanUtils.isTrueValue(record.repVolemica);
    const ressuscitCardio = BooleanUtils.isTrueValue(record.ressuscitCardio);
    const scMonitAtrioEsquerdo = BooleanUtils.isTrueValue(record.scMonitAtrioEsquerdo);
    const suepVentSupl = BooleanUtils.isTrueValue(record.suepVentSupl);
    const tecHemofiltracao = BooleanUtils.isTrueValue(record.tecHemofiltracao);
    const time = record.time;
    const tratFuncaoPulmonar = BooleanUtils.isTrueValue(record.tratFuncaoPulmonar);
    const vasoativaMulti = BooleanUtils.isTrueValue(record.vasoativaMulti);
    const vasoativaUnica = BooleanUtils.isTrueValue(record.vasoativaUnica);
    const ventMecanica = BooleanUtils.isTrueValue(record.ventMecanica);
    const viaVenosaCentral = BooleanUtils.isTrueValue(record.viaVenosaCentral);
    const viasAerias = BooleanUtils.isTrueValue(record.viasAerias);
    const evaluationDate = record.evaluationDate as string;

    const data = {...record,
      acidosis: acidoseAlcalose,
      cateterArtPerif, curativoFrequente,
      curativoRotina, diureseAtiva,
      dreno, evaluationDate, interForaUti,
      interMultiUti, interUnicaUti,
      laboratorio, medDebitoUrina,
      medicaUnica, medicEndovenosa,
      monitPadrao, nutEnteral,
      nutParent, pressaoIntracraniana,
      repVolemica, ressuscitCardio,
      scMonitAtrioEsquerdo, suepVentSupl,
      tecHemofiltracao, time,
      tratFuncaoPulmonar, vasoativaMulti,
      vasoativaUnica, ventMecanica,
      viasAerias, viaVenosaCentral,
    }

    this.formOneAPI.setData(data);
    this.formTwoAPI.setData(data);
    this.formThreeAPI.setData(data);

    this.checkAcidosisAPI.setValue(acidoseAlcalose);
    this.checkActiveDiuresisAPI.setValue(diureseAtiva);
    this.checkArtificialAirwayAPI.setValue(viasAerias);
    this.checkCardioRessuscitAPI.setValue(ressuscitCardio);
    this.checkCatArtPeriAPI.setValue(cateterArtPerif);
    this.checkCentralVenousAPI.setValue(viaVenosaCentral);
    this.checkDrainAPI.setValue(dreno);
    this.checkEntNutriAPI.setValue(nutEnteral);
    this.checkFreqDressingAPI.setValue(curativoFrequente);
    this.checkIntracranialAPI.setValue(pressaoIntracraniana);
    this.checkIntravenousMedAPI.setValue(medicEndovenosa);
    this.checkLabAPI.setValue(laboratorio);
    this.checkMechVentAPI.setValue(ventMecanica);
    this.checkMonitAtriumAPI.setValue(scMonitAtrioEsquerdo);
    this.checkMultiICUAPI.setValue(interMultiUti);
    this.checkMultivasoMedAPI.setValue(vasoativaMulti);
    this.checkOutICUAPI.setValue(interForaUti);
    this.checkParenNutriAPI.setValue(nutParent);
    this.checkRepoVolAPI.setValue(repVolemica);
    this.checkRoutineDressingAPI.setValue(curativoRotina);
    this.checkStandardMonitAPI.setValue(monitPadrao);
    this.checkSupVentSupportAPI.setValue(suepVentSupl);
    this.checkTechHemoAPI.setValue(tecHemofiltracao);
    this.checkTratPulmonarAPI.setValue(tratFuncaoPulmonar);
    this.checkUniqueICUAPI.setValue(interUnicaUti);
    this.checkUniqueMedAPI.setValue(medicaUnica);
    this.checkUrineDebitAPI.setValue(medDebitoUrina);
    this.checkVasoMedAPI.setValue(vasoativaUnica);
    this.setupDateInputApi.setValue(evaluationDate);
  }

  private createNewsTISS28Score(body: Record<string, Value>): void {
    this.formThreeAPI.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.post(Endpoint.POST_CREATE_SCORE_TISS_28, undefined, { abortController, body})
      .then(response => {
        const {idScoreIndexes} = response.body as ResponseCreateScoreTISS28;
        this.showModalSubmit(idScoreIndexes);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formThreeAPI.setLoading(false));
  }

  @OnBeforeSubmit({ target: ScoreTiss_28EditAliases.WIZARD })
  handleBeforeSubmit(): void {
    const isCreated = this.context.get('isCreated');
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formDataOne   = this.formOneAPI.getRecord();
    const formDataTwo   = this.formTwoAPI.getRecord();
    const formDataThree = this.formThreeAPI.getRecord();
    const acidoseAlcalose = BooleanUtils.isPositiveValue(formDataTwo.acidosis as boolean);
    const cateterArtPerif = BooleanUtils.isPositiveValue(formDataThree.cateterArtPerif as boolean);
    const curativoFrequente = BooleanUtils.isPositiveValue(formDataOne.curativoFrequente as boolean);
    const curativoRotina = BooleanUtils.isPositiveValue(formDataOne.curativoRotina as boolean);
    const diureseAtiva = BooleanUtils.isPositiveValue(formDataTwo.diureseAtiva as boolean);
    const dreno = BooleanUtils.isPositiveValue(formDataOne.dreno as boolean);
    const interForaUti = BooleanUtils.isPositiveValue(formDataThree.interForaUti as boolean);
    const interMultiUti = BooleanUtils.isPositiveValue(formDataThree.interMultiUti as boolean);
    const interUnicaUti = BooleanUtils.isPositiveValue(formDataThree.interUnicaUti as boolean);
    const laboratorio = BooleanUtils.isPositiveValue(formDataOne.laboratorio as boolean);
    const medDebitoUrina = BooleanUtils.isPositiveValue(formDataTwo.medDebitoUrina as boolean);
    const medicEndovenosa = BooleanUtils.isPositiveValue(formDataOne.medicEndovenosa as boolean);
    const medicaUnica = BooleanUtils.isPositiveValue(formDataOne.medicaUnica as boolean);
    const monitPadrao = BooleanUtils.isPositiveValue(formDataOne.monitPadrao as boolean);
    const nutEnteral = BooleanUtils.isPositiveValue(formDataTwo.nutEnteral as boolean);
    const nutParent = BooleanUtils.isPositiveValue(formDataTwo.nutParent as boolean);
    const pressaoIntracraniana = BooleanUtils.isPositiveValue(formDataTwo.pressaoIntracraniana as boolean);
    const repVolemica = BooleanUtils.isPositiveValue(formDataThree.repVolemica as boolean);
    const ressuscitCardio = BooleanUtils.isPositiveValue(formDataThree.ressuscitCardio as boolean);
    const scMonitAtrioEsquerdo = BooleanUtils.isPositiveValue(formDataThree.scMonitAtrioEsquerdo as boolean);
    const suepVentSupl = BooleanUtils.isPositiveValue(formDataTwo.suepVentSupl as boolean);
    const tecHemofiltracao = BooleanUtils.isPositiveValue(formDataTwo.tecHemofiltracao as boolean);
    const time =  String((formDataOne.time as DomainType).valueDomain);
    const tratFuncaoPulmonar = BooleanUtils.isPositiveValue(formDataTwo.tratFuncaoPulmonar as boolean);
    const vasoativaMulti = BooleanUtils.isPositiveValue(formDataThree.vasoativaMulti as boolean);
    const vasoativaUnica = BooleanUtils.isPositiveValue(formDataThree.vasoativaUnica as boolean);
    const ventMecanica = BooleanUtils.isPositiveValue(formDataTwo.ventMecanica as boolean);
    const viaVenosaCentral = BooleanUtils.isPositiveValue(formDataThree.viaVenosaCentral as boolean);
    const viasAerias = BooleanUtils.isPositiveValue(formDataTwo.viasAerias as boolean);
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    const evaluationDate = moment(formDataOne.evaluationDate as string).utc().format(ISO_FULL_DATE_FORMAT);

    const body = {
      acidoseAlcalose,
      attendance: encounterId as number,
      cateterArtPerif,
      curativoFrequente,
      curativoRotina,
      diureseAtiva,
      dreno,
      evaluationDate,
      idScoreIndexes: idScoreIndexes as number,
      interForaUti,
      interMultiUti,
      interUnicaUti,
      laboratorio,
      medDebitoUrina,
      medicaUnica,
      medicEndovenosa,
      monitPadrao,
      nutEnteral,
      nutParent,
      pessoaFisica: user.physicalPersonId,
      pressaoIntracraniana,
      repVolemica,
      ressuscitCardio,
      scMonitAtrioEsquerdo,
      suepVentSupl,
      tecHemofiltracao,
      time,
      tratFuncaoPulmonar,
      vasoativaMulti,
      vasoativaUnica,
      ventMecanica,
      viasAerias,
      viaVenosaCentral,
    } as Record<string, Value>;
    this.createNewsTISS28Score(body);
  }

  @OnClick({ target: ScoreTiss_28EditAliases.ACTION_NEW_SCORE_TISS_28 })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.SCORE_TISS_28_CREATE, {
      encounterId,
      patientId,
    })
  }

  @OnClick({ target: ScoreTiss_28EditAliases.ACTION_RELEASE_SCORE_TISS_28 })
  handleClickRelease(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreTiss_28EditAliases.ACTION_DELETE_SCORE_TISS_28 })
  handleClickDelete(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(idScoreIndexes as number);
  }

  @OnClick({ target: ScoreTiss_28EditAliases.ACTION_REFERENCE_SCORE_TISS_28 })
  handleClickReference(): void {
    this.getReference();
  }

  @OnUnmount(ScoreTiss_28EditAliases.SCORE_TISS_28_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_TISS_28;
  }
}
